import type { TDateFormat } from '@/types';

import { useState, useEffect } from 'react';

import Button from '@/components/Button/Button';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { useFormatText } from '@/hooks/useFormatText';
import {
  getUserSettings,
  updateLanguageRegion,
} from '@/service/settingsService';
import { useSettingsStore } from '@/store/useSettingsStore';

const LANGUAGE_OPTIONS = [
  { id: 'en', name: 'English' },
  { id: 'ar', name: 'العربية' },
];

const DATE_FORMAT_OPTIONS = [
  { value: 'dd-mm-yyyy', label: 'DD/MM/YYYY' },
  { value: 'mm-dd-yyyy', label: 'MM/DD/YYYY' },
  { value: 'yyyy-mm-dd', label: 'YYYY/MM/DD' },
];

const LanguageRegion = () => {
  const { formatText } = useFormatText();
  const { settings, updateSettings } = useSettingsStore();

  const [language, setLanguage] = useState(settings.language);
  const [dateFormat, setDateFormat] = useState<TDateFormat>(
    settings.dateFormat
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getUserSettings();
        if (response?.data) {
          const { language, date_format } = response.data;

          setLanguage(language);
          setDateFormat(date_format as TDateFormat);

          if (language === 'en' || language === 'ar') {
            updateSettings({
              language,
              direction: language === 'ar' ? 'rtl' : 'ltr',
              dateFormat: date_format as TDateFormat,
            });
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLanguage(settings.language);
    setDateFormat(settings.dateFormat);
  }, [settings.language, settings.dateFormat]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const newSettings = {
        language: language as 'en' | 'ar',
        date_format: dateFormat,
      };

      const response = await updateLanguageRegion(newSettings);

      if (response?.status) {
        updateSettings({
          language: language as 'en' | 'ar',
          direction: language === 'ar' ? 'rtl' : 'ltr',
          dateFormat,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-216 w-full" data-testid="language-region-container">
      {/* Section Title */}
      <h2 className="text-xl font-semibold text-neutral-primary leading-7 tracking-tight">
        {formatText('settings.language-region.title')}
      </h2>

      {/* Card  */}
      <div className="mt-6 border border-neutral-light-200 rounded-xl">
        <div className="p-6 flex flex-col gap-12">
          {/* Row 1: Language */}
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <label className="text-base font-semibold text-neutral-primary">
                {formatText('settings.language-region.default-language')}
              </label>
              <Select
                options={LANGUAGE_OPTIONS}
                selected={language}
                onChange={(val) => setLanguage(val as 'en' | 'ar')}
                placeholder={formatText('common.select')}
              />
            </div>
          </div>

          {/* Row 2: Date Format & Currency */}
          <div className="grid grid-cols-2 gap-6">
            <RadioButtonGroup
              name="dateFormat"
              label={formatText('settings.language-region.date-format')}
              options={DATE_FORMAT_OPTIONS}
              value={dateFormat}
              onChange={(val) => setDateFormat(val as TDateFormat)}
              direction="row"
              gap="gap-3"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6">
        <Button
          label={formatText('common.actions.save-changes')}
          variant="primary"
          onClick={handleSave}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default LanguageRegion;
